import { ChangeDetectionStrategy, Component, inject, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { NgxSpinnerService } from 'ngx-spinner';
import { finalize, first, Subject, takeUntil } from 'rxjs';
import { ErrorCode } from 'src/app/common/error-codes/ErrorCode';
import { SplitComponentEnum } from 'src/app/common/enums/split-components';
import { AnnexService } from 'src/app/data/annex.service';
import {
  AnnexDetailsAnnexEntry,
  AnnexDetailsDto,
  AnnexDetailsWagesEntry,
  AnnexDetailsWorkerAgreementEntry,
} from 'src/app/models/dtos/annexes/annex-details-dto';
import { AnnexListDto } from 'src/app/models/dtos/annexes/annex-list-dto';
import { FileService } from 'src/app/shared/services/file-service';
import { SnackBarService } from 'src/app/shared/services/snack-bar.service';
import { SplitComponentService } from 'src/app/split/services/split-component.service';
import { AnnexFilePanelComponent } from './annex-file-panel.component/annex-file-panel.component';

@Component({
  selector: 'app-annex-details',
  templateUrl: './annex-details.component.html',
  styleUrl: './annex-details.component.scss',
  standalone: false,
})
export class AnnexDetailsComponent implements OnInit, OnDestroy {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private splitComponentService = inject(SplitComponentService);
  private annexService = inject(AnnexService);
  private spinner = inject(NgxSpinnerService);
  private fileService = inject(FileService);
  private snackbarService = inject(SnackBarService);

  splitComponentId = 'annex-details-split';
  annexId: number;
  agreementId: number;
  employmentTypeId: number;
  workerId: number;
  private destroy$ = new Subject<void>();

  annexDetails: AnnexDetailsDto | null = null;
  allAnnexes: AnnexListDto[] = [];
  previousAnnexId: number | null = null;
  nextAnnexId: number | null = null;
  loading = false;

  ngOnInit(): void {
    this.route.params.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      this.annexId = +params['annexId'];
      this.agreementId = +params['agreementId'];
      this.workerId = +params['workerId'];
      this.employmentTypeId = +params['employmentTypeId'];
      this.loadAnnexDetails();
    });
  }

  ngOnDestroy(): void {
    this.splitComponentService.destroySplitter(this.splitComponentId);
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadAnnexDetails(): void {
    this.spinner.show();

    this.annexService
      .getAnnexDetails(this.annexId)
      .pipe(finalize(() => this.spinner.hide()))
      .subscribe((details) => {
        this.annexDetails = details;
        this.loadAnnexesForNavigation(details.Annex.WorkerAgreementId);
        if (details.Annex.IsImported) {
          this.removePdfPanel();
        } else {
          this.refreshPdfPanel();
        }
      });
  }

  private refreshPdfPanel(): void {
    this.removePdfPanel();

    this.splitComponentService.addComponent(this.splitComponentId, {
      id: SplitComponentEnum.AnnexFilePreview,
      componentType: AnnexFilePanelComponent,
      direction: 'horizontal',
      data: {
        annexId: this.annexId,
      },
      size: 45,
      header: {
        closeButton: false,
      },
    });
  }

  private removePdfPanel(): void {
    const existingComponents = this.splitComponentService.getComponentsConfigs(this.splitComponentId);
    const panelExists = existingComponents?.some(
      (component) => component.id === SplitComponentEnum.AnnexFilePreview
    );

    if (panelExists) {
      this.splitComponentService.removeComponent(
        this.splitComponentId,
        SplitComponentEnum.AnnexFilePreview
      );
    }
  }

  private loadAnnexesForNavigation(workerAgreementId: number): void {
    this.annexService.getAnnexesList(workerAgreementId, true).subscribe(annexes => {
      this.allAnnexes = annexes;
      this.calculateNavigation();
    });
  }

  private calculateNavigation(): void {
    const currentIndex = this.allAnnexes.findIndex((a) => a.AnnexId === this.annexId);
    this.previousAnnexId = currentIndex > 0 ? this.allAnnexes[currentIndex - 1].AnnexId : null;
    this.nextAnnexId = currentIndex < this.allAnnexes.length - 1 ? this.allAnnexes[currentIndex + 1].AnnexId : null;
  }

  goToAgreement(): void {
    this.router.navigate(['/workers', this.workerId, 'employmentType', this.employmentTypeId, 'agreements', this.agreementId, 'details']);
  }

  goToPreviousAnnex(): void {
    if (this.previousAnnexId) {
      this.router.navigate(['/workers', this.workerId, 'employmentType', this.employmentTypeId, 'agreements', this.agreementId, 'annexes', this.previousAnnexId, 'details']);
    }
  }

  goToNextAnnex(): void {
    if (this.nextAnnexId) {
      this.router.navigate(['/workers', this.workerId, 'employmentType', this.employmentTypeId, 'agreements', this.agreementId, 'annexes', this.nextAnnexId, 'details']);
    }
  }

  onDownloadAnnexClick(): void {
    this.annexService
      .showAnnexDocument(this.annexId)
      .pipe(first())
      .subscribe({
        next: (response) => {
          const blob = response.body;
          if (!blob) {
            this.snackbarService.openErrorSnackBar(ErrorCode.SomethingWentWrong);
            return;
          }
          this.fileService.showFile(
            'aneks_do_umowy_' + this.agreementId + '_' + new Date().getTime() + '.pdf',
            undefined,
            undefined,
            blob,
          );
        },
        error: (_error: HttpErrorResponse) => {
          this.snackbarService.openErrorSnackBar(ErrorCode.SomethingWentWrong);
        },
      });
  }
}
